import type { BlockProcess } from 'types/block-process';

import React from 'react';

import { Paragraph, Wrap } from '@leafwell/components';

import HeadingBody from './heading-body';
import Figure from './figure';

const HORIZONTAL_THRESHOLD = 4;

const Process: React.FC<BlockProcess> = ({
  sectionId,
  instanceId,
  title,
  description,
  label,
  labelIcon,
  items,
}) => {
  const isHorizontal = items?.length <= HORIZONTAL_THRESHOLD;
  return (
    <Wrap id={sectionId} itemID={`Process-${instanceId}`}>
      {title || description ? (
        <div className="grid gap-y-6 justify-items-center">
          {title && (
            <HeadingBody
              level="2"
              tagName="h2"
              title={title}
              className="font-semibold text-center"
            />
          )}
          {description && (
            <Paragraph
              dangerouslySetInnerHTML={{ __html: description }}
              size="large"
              className="text-center lg:max-w-5xl"
            />
          )}
        </div>
      ) : null}
      {label ? (
        <div className="mx-auto w-fit flex items-center gap-x-2 justify-center py-3 px-6 bg-green-50 border border-green rounded-lg">
          {labelIcon ? <Figure {...labelIcon} /> : null}
          <Paragraph size="small" className="font-medium text-green-600">
            {label}
          </Paragraph>
        </div>
      ) : null}
      {items?.length > 0 ? (
        <>
          {isHorizontal && (
            <ol className="hidden lg:flex w-full relative">
              <div className="absolute top-8 left-36 right-36 h-1.5 bg-lilac-300 -translate-y-1/2" />
              {items.map((item, index) => (
                <li
                  key={`item-${index}-${instanceId}`}
                  className={[
                    'flex-1 flex flex-col items-center gap-4 relative z-10',
                    index === items.length - 1 ? '' : 'pr-2',
                  ].join(' ')}
                >
                  <div className="w-16 h-16 rounded-full bg-lilac-300 font-medium text-base flex items-center justify-center shrink-0">
                    {index + 1}
                  </div>
                  <div className="text-center">
                    <Paragraph
                      size="large"
                      tagName="h3"
                      className="font-semibold text-purple-500 mb-1.5"
                      dangerouslySetInnerHTML={{ __html: item.itemTitle }}
                    />
                    <Paragraph
                      className="text-gray-700"
                      dangerouslySetInnerHTML={{ __html: item.itemContent }}
                    />
                  </div>
                </li>
              ))}
            </ol>
          )}
          <ol
            className={
              isHorizontal
                ? 'flex flex-col lg:hidden w-full'
                : 'flex flex-col w-full'
            }
          >
            {items.map((item, index) => (
              <li key={`item-${index}-${instanceId}`} className="flex gap-4">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-10 h-10 rounded-full bg-lilac-300  font-medium text-base flex items-center justify-center">
                    {index + 1}
                  </div>
                  {index < items.length - 1 && (
                    <div className="w-1.5 flex-1 bg-lilac-300 min-h-6" />
                  )}
                </div>
                <div
                  className={`pt-0.5 ${index < items.length - 1 ? 'pb-7' : ''}`}
                >
                  <Paragraph
                    size="large"
                    tagName="h3"
                    className="font-semibold text-purple-500 mb-1"
                    dangerouslySetInnerHTML={{ __html: item.itemTitle }}
                  />
                  <Paragraph
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: item.itemContent }}
                  />
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </Wrap>
  );
};

export default Process;
